import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ATTRIBUTION_TAB_IDS, POSTBACK_EVENT_TYPE_OPTIONS } from './consts';
import IntegrationRequestService from './service';

const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;
const URL_VALIDATE_DEBOUNCE_MS = 400;

export const urlField = eventType => `${eventType}_postback_url`;
export const POSTBACK_URL_FIELDS = ['postback_url_base', ...POSTBACK_EVENT_TYPE_OPTIONS.map(urlField)];

const service = new IntegrationRequestService();

// Debounced POST /api/partner-integration/request/validate-url/ — mirrors WebsiteField.
// Returns null while empty/pending, true/false once resolved.
const useApiUrlValidity = value => {
    const [valid, setValid] = useState(null);
    const seqRef = useRef(0);
    useEffect(() => {
        const trimmed = (value || '').trim();
        if (!trimmed) {
            setValid(null);
            return undefined;
        }
        const seq = ++seqRef.current;
        const timer = setTimeout(() => {
            service
                .validatePostbackUrl(trimmed)
                .then(res => {
                    if (seq === seqRef.current) setValid(Boolean(res && res.valid));
                })
                .catch(() => {
                    if (seq === seqRef.current) setValid(false);
                });
        }, URL_VALIDATE_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [value]);
    return valid;
};

// Hooks must be called in a stable order → one call per fixed URL field.
export const useFieldValidityValues = formData => {
    const tab = formData?.[TAB_ID] || {};
    return {
        postback_url_base: useApiUrlValidity(tab.postback_url_base),
        conversions_postback_url: useApiUrlValidity(tab.conversions_postback_url),
        post_install_events_postback_url: useApiUrlValidity(tab.post_install_events_postback_url),
        revenue_events_postback_url: useApiUrlValidity(tab.revenue_events_postback_url),
        fraud_postbacks_postback_url: useApiUrlValidity(tab.fraud_postbacks_postback_url),
    };
};

const FieldValidityContext = createContext({});

export const FieldValidityProvider = FieldValidityContext.Provider;

export const useFieldValidityContext = () => useContext(FieldValidityContext);
